/**
 * The Documents tab on Tax & compliance (doc 04-backend/30 §4.4): the gap audit, the invoice
 * register, the GSTR-1 export and the re-render.
 *
 * **Built from the spec, not a design.** `screens-tax.jsx` has no register or gap-audit view; the
 * owner ruled on 2026-10-26 that design-less screens are built from the docs with the console's
 * own components and restyled later.
 *
 * The gap audit leads because it is the one read that matters most: a hole in a statutory series
 * cannot be closed after the fact, only explained, and explaining needs knowing in time. Nothing
 * here edits a filed document — the server refuses an UPDATE on an issued row, so a button that
 * offered one would always fail. A correction is a credit note: a new document, always four-eyes.
 */
import { useState } from 'react';
import { Chip, StatusChip } from '../components/primitives';
import { Input } from '../components/forms';
import { DataTable, Pager } from '../components/data';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, FilterBar, Money, ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { formatInr, parsePaise } from '../lib/money';
import { newIdempotencyKey } from '../lib/api/idempotency';
import {
  useCreateCreditNote,
  useDocumentExport,
  useDocumentRegister,
  useGapAudit,
  useReRender,
  type InvoiceRow,
} from '../lib/api/hooks/documents';

const PAGE_SIZE = 50;
const FY = /^\d{4}-\d{2}$/;

/** The server's three document types, in its words, with the counterparty each is addressed to. */
export const DOCUMENT_TYPES = [
  { key: '', label: 'All' },
  { key: 'commission_to_provider', label: 'Commission → pandit' },
  { key: 'platform_fee_to_customer', label: 'Platform fee → devotee' },
  { key: 'booking_summary', label: 'Booking summary' },
] as const;

export function typeLabel(type: string): string {
  return DOCUMENT_TYPES.find((t) => t.key === type)?.label ?? type;
}

/** CGST + SGST for an intra-state supply, IGST for an inter-state one — one of the two is zero. */
export function taxOf(row: InvoiceRow): number {
  return row.cgst_total_paise + row.sgst_total_paise + row.igst_total_paise;
}

/**
 * What stops a credit note from being filed, phrased to follow "first add". The amount is the
 * inclusive reversal — the server carves out the tax heads at the invoice's own rate — so it may
 * not exceed the invoice total. The seconder is a different finance admin's id; the server checks
 * it is not the requester's.
 */
export function creditNoteProblems(amount: string, seconder: string, grandTotalPaise: number): string[] {
  const problems: string[] = [];
  const paise = parsePaise(amount);
  if (paise === null || paise <= 0 || paise > grandTotalPaise) {
    problems.push(`a reversal amount above ₹0 and at most ${formatInr(grandTotalPaise, { paise: true })}`);
  }
  if (!/^[1-9]\d*$/.test(seconder.trim())) problems.push('the admin id of the finance colleague seconding this');
  return problems;
}

export function TaxDocuments({ initialFy }: { initialFy: string }) {
  const { scope, toast } = useConsole();
  const [fy, setFy] = useState(initialFy);
  const [type, setType] = useState('');
  const [page, setPage] = useState(1);
  const [rendering, setRendering] = useState<InvoiceRow | null>(null);
  const [crediting, setCrediting] = useState<InvoiceRow | null>(null);
  const [amount, setAmount] = useState('');
  const [seconder, setSeconder] = useState('');
  const [key, setKey] = useState('');
  const valid = FY.test(fy);

  const audit = useGapAudit(fy, valid);
  const register = useDocumentRegister(
    { fy, page, page_size: PAGE_SIZE, ...(type ? { invoice_type: type } : {}) },
    valid,
  );
  const exportRegister = useDocumentExport();
  const reRender = useReRender();
  const creditNote = useCreateCreditNote();
  const startCredit = (row: InvoiceRow) => {
    setAmount('');
    setSeconder('');
    setKey(newIdempotencyKey());
    setCrediting(row);
  };
  const fail = (error: unknown) => toast({ tone: 'error', message: errorMessage(error) });

  return (
    <>
      <FilterBar
        extra={
          <>
            <Input
              label="Financial year"
              value={fy}
              onChange={(value) => {
                setFy(value);
                setPage(1);
              }}
              error={valid ? undefined : 'A financial year reads 2026-27.'}
            />
            {DOCUMENT_TYPES.map((t) => (
              <Chip
                key={t.key}
                variant="filter"
                selected={type === t.key}
                label={t.label}
                onClick={() => {
                  setType(t.key);
                  setPage(1);
                }}
              />
            ))}
          </>
        }
      />

      {valid && (
        <Section title="Gap audit" note="Issued count against the highest number against the counter, per series">
          {audit.isPending && <LoadingState message="Auditing the series…" />}
          {audit.isError && <ErrorState title="Could not audit the series" cause={errorMessage(audit.error)} />}
          {audit.isSuccess && audit.data.counters.length === 0 && (
            <EmptyState inline title="No series this year" body="Nothing has been numbered in this financial year yet." />
          )}
          {audit.isSuccess && audit.data.counters.length > 0 && (
            <>
              <p style={{ margin: '0 0 var(--sp-sm)', fontSize: 'var(--fs-admin-ui)' }}>
                {audit.data.is_clean ? (
                  <StatusChip tone="success" label="Every series is gapless" />
                ) : (
                  <StatusChip tone="error" label="A series has a hole — explain it before the return is filed" />
                )}
              </p>
              <DataTable
                density="compact"
                columns={[
                  { key: 'series', label: 'Series' },
                  { key: 'issued', label: 'Issued', align: 'right', mono: true },
                  { key: 'highest', label: 'Highest number', align: 'right', mono: true },
                  { key: 'next', label: 'Counter next', align: 'right', mono: true },
                  { key: 'state', label: 'State' },
                ]}
                rows={audit.data.counters.map((c) => ({
                  id: `${c.financial_year}-${c.document_type}`,
                  series: `${c.document_type} · ${c.financial_year}`,
                  issued: c.issued_count,
                  highest: c.highest_value,
                  next: c.next_number,
                  state: c.is_gapless ? <StatusChip tone="success" label="gapless" /> : <StatusChip tone="error" label="gap" />,
                }))}
              />
            </>
          )}
        </Section>
      )}

      {valid && (
        <Section
          title="Invoice register"
          note="Every issued document, never edited — a correction is a credit note"
          pad={false}
          actions={
            <ScopeAction
              scope={scope}
              need="finance"
              size="sm"
              variant="ghost"
              onClick={() =>
                exportRegister.mutate(
                  { fy },
                  {
                    onSuccess: (csv) => {
                      // Handed over as the file a CA files GSTR-1 from, not a table to copy out of.
                      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `invoice-register-${fy}.csv`;
                      link.click();
                      URL.revokeObjectURL(url);
                      toast({ tone: 'success', message: 'Register exported' });
                    },
                    onError: fail,
                  },
                )
              }
            >
              Export GSTR-1 register
            </ScopeAction>
          }
        >
          {register.isPending && <LoadingState message="Reading the register…" />}
          {register.isError && <ErrorState title="Could not read the register" cause={errorMessage(register.error)} />}
          {register.isSuccess && register.data.items.length === 0 && (
            <EmptyState inline title="No documents" body="Nothing of this type was issued in this financial year." />
          )}
          {register.isSuccess && register.data.items.length > 0 && (
            <>
              <DataTable
                density="compact"
                columns={[
                  { key: 'number', label: 'Number', mono: true },
                  { key: 'type', label: 'Type' },
                  { key: 'status', label: 'Status' },
                  { key: 'pos', label: 'Place of supply' },
                  { key: 'taxable', label: 'Taxable', align: 'right', mono: true },
                  { key: 'tax', label: 'GST', align: 'right', mono: true },
                  { key: 'total', label: 'Total', align: 'right', mono: true },
                  { key: 'pdf', label: 'Actions' },
                ]}
                rows={register.data.items.map((row) => ({
                  id: row.id,
                  number: row.invoice_number ?? row.public_code,
                  type: typeLabel(row.invoice_type),
                  status: row.status.replace(/_/g, ' '),
                  pos: row.place_of_supply,
                  taxable: <Money paise={row.taxable_total_paise} showPaise />,
                  tax: <Money paise={taxOf(row)} showPaise />,
                  total: <Money paise={row.grand_total_paise} showPaise bold />,
                  pdf: (
                    <>
                      <ScopeAction scope={scope} need="finance" size="sm" variant="ghost" onClick={() => setRendering(row)}>
                        {row.is_downloadable ? 'Re-render' : 'Render'}
                      </ScopeAction>
                      {row.status === 'issued' && (
                        <ScopeAction scope={scope} need="finance" size="sm" variant="ghost" onClick={() => startCredit(row)}>
                          Credit note
                        </ScopeAction>
                      )}
                    </>
                  ),
                }))}
              />
              <Pager page={page} pageSize={PAGE_SIZE} total={register.data.total} onPage={setPage} />
            </>
          )}
        </Section>
      )}

      {rendering !== null && (
        <ConfirmAction
          open
          title={`Draw the PDF of ${rendering.invoice_number ?? rendering.public_code} again?`}
          phrase={rendering.public_code}
          reason="Why it is being drawn again"
          consequences={[
            'The figures are not recomputed: the PDF is drawn from the filed rows, and the server refuses if the new file would say anything different.',
            'Recorded against your name in the audit log.',
          ]}
          confirmLabel="Re-render"
          onCancel={() => setRendering(null)}
          onConfirm={(reason) =>
            reRender.mutate(
              { code: rendering.public_code, reason },
              {
                onSuccess: () => {
                  setRendering(null);
                  toast({ tone: 'success', message: 'Drawn again — the file is unchanged in substance' });
                },
                onError: (e) => {
                  setRendering(null);
                  fail(e);
                },
              },
            )
          }
        />
      )}
      {crediting !== null && (
        <ConfirmAction
          open
          title={`Credit note against ${crediting.invoice_number ?? crediting.public_code}?`}
          phrase={crediting.public_code}
          tone="error"
          reason="Why the filed document is being corrected"
          blocked={creditNoteProblems(amount, seconder, crediting.grand_total_paise)}
          consequences={[
            'A classification fix with no money moving — a refund issues its own credit note.',
            'Files a new numbered document that reverses part or all of this one. It changes what the GST return says, and cannot be withdrawn.',
            'Four-eyes, whatever the amount: a different finance admin seconds it, and both names are kept in the audit log.',
          ]}
          confirmLabel="File credit note"
          onCancel={() => setCrediting(null)}
          onConfirm={(reason) =>
            creditNote.mutate(
              {
                invoice_code: crediting.public_code,
                total_reversal_paise: parsePaise(amount)!,
                reason,
                second_approver_admin_id: Number(seconder.trim()),
                key,
              },
              {
                onSuccess: (note) => {
                  setCrediting(null);
                  toast({ tone: 'success', message: `Credit note ${note.credit_note_number} filed` });
                },
                // Kept open: a refused seconder is fixed in place, and the key still guards a retry.
                onError: fail,
              },
            )
          }
        >
          <Input
            prefix="₹"
            inputMode="decimal"
            label="Reversal, tax inclusive"
            value={amount}
            onChange={setAmount}
            helper={`At most ${formatInr(crediting.grand_total_paise, { paise: true })}; the tax heads are split at the invoice's own rate`}
          />
          <Input inputMode="numeric" label="Seconded by (admin id)" value={seconder} onChange={setSeconder} />
        </ConfirmAction>
      )}
    </>
  );
}
